import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import type { PlatformMessage } from './types';

const STORAGE_PREFIX = 'sasity:platform-messages:last-acknowledged:v1';
const ACKNOWLEDGEMENT_EVENT = 'sasity:platform-message-acknowledged';

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${encodeURIComponent(userId)}`;
}

function readAcknowledgement(userId: string | undefined): string | null {
  if (!userId) return null;
  try {
    return localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

function latestPublishedAt(messages: PlatformMessage[]): string | undefined {
  return messages.reduce<string | undefined>(
    (latest, message) => !latest || message.published_at > latest ? message.published_at : latest,
    undefined,
  );
}

export function useMessageAcknowledgement() {
  const { user } = useAuth();
  const userId = user?.userId || user?.username;
  const [, setRevision] = useState(0);
  const [volatileAcknowledgement, setVolatileAcknowledgement] = useState<{
    userId: string;
    publishedAt: string;
  } | null>(null);
  const persistedAcknowledgement = readAcknowledgement(userId);
  const volatilePublishedAt = volatileAcknowledgement && volatileAcknowledgement.userId === userId
    ? volatileAcknowledgement.publishedAt
    : null;
  const acknowledgedAt = persistedAcknowledgement
    ?? volatilePublishedAt;

  useEffect(() => {
    const refresh = () => setRevision((current) => current + 1);
    const handleStorage = (event: StorageEvent) => {
      if (userId && event.key === storageKey(userId)) refresh();
    };
    window.addEventListener(ACKNOWLEDGEMENT_EVENT, refresh);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(ACKNOWLEDGEMENT_EVENT, refresh);
      window.removeEventListener('storage', handleStorage);
    };
  }, [userId]);

  const acknowledgeLatest = useCallback((messages: PlatformMessage[]) => {
    const publishedAt = latestPublishedAt(messages);
    if (!userId || !publishedAt) return;
    setVolatileAcknowledgement({ userId, publishedAt });
    try {
      localStorage.setItem(storageKey(userId), publishedAt);
      window.dispatchEvent(new Event(ACKNOWLEDGEMENT_EVENT));
    } catch {
      // Acknowledgement persistence is non-critical when storage is unavailable.
    }
  }, [userId]);

  const isUnacknowledged = useCallback(
    (message: PlatformMessage) => !acknowledgedAt || message.published_at > acknowledgedAt,
    [acknowledgedAt],
  );

  return { acknowledgeLatest, isUnacknowledged };
}
